/**
 * Language flag emoji mappings for OpenAI and Azure OpenAI Realtime APIs
 * Maps language codes to their corresponding country flag emojis
 * 
 * - OpenAI uses ISO 639-1 (2-letter codes): en, ms, zh, etc.
 * - Azure uses locale codes (language-country): en-US, ms-MY, zh-CN, etc.
 */

/**
 * OpenAI Realtime API language flags (58 languages)
 * Uses 2-letter ISO 639-1 language codes
 * Mapped to most representative country for each language
 */
export const OPENAI_LANGUAGE_FLAGS: Record<string, string> = {
  // A
  'af': '🇿🇦', // Afrikaans → South Africa
  'ar': '🇸🇦', // Arabic → Saudi Arabia (Standard Arabic)
  'hy': '🇦🇲', // Armenian → Armenia
  'az': '🇦🇿', // Azerbaijani → Azerbaijan
  
  // B
  'be': '🇧🇾', // Belarusian → Belarus
  'bs': '🇧🇦', // Bosnian → Bosnia and Herzegovina
  'bg': '🇧🇬', // Bulgarian → Bulgaria
  
  // C
  'ca': '🇪🇸', // Catalan → Spain (Catalonia)
  'zh': '🇨🇳', // Chinese → China (Simplified Chinese)
  'hr': '🇭🇷', // Croatian → Croatia
  'cs': '🇨🇿', // Czech → Czech Republic
  'cy': '🇬🇧', // Welsh → United Kingdom (Wales)
  
  // D
  'da': '🇩🇰', // Danish → Denmark
  'nl': '🇳🇱', // Dutch → Netherlands
  
  // E
  'en': '🇺🇸', // English → United States (most common variant)
  'et': '🇪🇪', // Estonian → Estonia
  
  // F
  'fi': '🇫🇮', // Finnish → Finland
  'fr': '🇫🇷', // French → France
  
  // G
  'gl': '🇪🇸', // Galician → Spain (Galicia)
  'de': '🇩🇪', // German → Germany
  'el': '🇬🇷', // Greek → Greece
  
  // H
  'he': '🇮🇱', // Hebrew → Israel
  'hi': '🇮🇳', // Hindi → India
  'hu': '🇭🇺', // Hungarian → Hungary
  
  // I
  'is': '🇮🇸', // Icelandic → Iceland
  'id': '🇮🇩', // Indonesian → Indonesia
  'it': '🇮🇹', // Italian → Italy
  
  // J
  'ja': '🇯🇵', // Japanese → Japan
  
  // K
  'kn': '🇮🇳', // Kannada → India
  'kk': '🇰🇿', // Kazakh → Kazakhstan
  'ko': '🇰🇷', // Korean → South Korea
  
  // L
  'lv': '🇱🇻', // Latvian → Latvia
  'lt': '🇱🇹', // Lithuanian → Lithuania
  
  // M
  'mk': '🇲🇰', // Macedonian → North Macedonia
  'ms': '🇲🇾', // Malay → Malaysia
  'mr': '🇮🇳', // Marathi → India
  'mi': '🇳🇿', // Maori → New Zealand
  
  // N
  'ne': '🇳🇵', // Nepali → Nepal
  'no': '🇳🇴', // Norwegian → Norway
  
  // P
  'fa': '🇮🇷', // Persian → Iran
  'pl': '🇵🇱', // Polish → Poland
  'pt': '🇵🇹', // Portuguese → Portugal
  
  // R
  'ro': '🇷🇴', // Romanian → Romania
  'ru': '🇷🇺', // Russian → Russia
  
  // S
  'sr': '🇷🇸', // Serbian → Serbia
  'sk': '🇸🇰', // Slovak → Slovakia
  'sl': '🇸🇮', // Slovenian → Slovenia
  'es': '🇪🇸', // Spanish → Spain
  'sw': '🇰🇪', // Swahili → Kenya
  'sv': '🇸🇪', // Swedish → Sweden
  
  // T
  'tl': '🇵🇭', // Tagalog → Philippines
  'ta': '🇮🇳', // Tamil → India
  'th': '🇹🇭', // Thai → Thailand
  'tr': '🇹🇷', // Turkish → Turkey
  
  // U
  'uk': '🇺🇦', // Ukrainian → Ukraine
  'ur': '🇵🇰', // Urdu → Pakistan
  
  // V
  'vi': '🇻🇳', // Vietnamese → Vietnam
}

/**
 * Azure OpenAI Realtime API language flags (94 languages)
 * Uses locale codes (language-country format)
 * Country code is extracted directly from the locale
 */
export const AZURE_LANGUAGE_FLAGS: Record<string, string> = {
  // Afrikaans
  'af-ZA': '🇿🇦', // South Africa
  
  // Amharic
  'am-ET': '🇪🇹', // Ethiopia
  
  // Arabic
  'ar-AE': '🇦🇪', // United Arab Emirates
  'ar-BH': '🇧🇭', // Bahrain
  'ar-DZ': '🇩🇿', // Algeria
  'ar-EG': '🇪🇬', // Egypt
  'ar-IQ': '🇮🇶', // Iraq
  'ar-JO': '🇯🇴', // Jordan
  'ar-KW': '🇰🇼', // Kuwait
  'ar-LB': '🇱🇧', // Lebanon
  'ar-LY': '🇱🇾', // Libya
  'ar-MA': '🇲🇦', // Morocco
  'ar-OM': '🇴🇲', // Oman
  'ar-QA': '🇶🇦', // Qatar
  'ar-SA': '🇸🇦', // Saudi Arabia
  'ar-SY': '🇸🇾', // Syria
  'ar-TN': '🇹🇳', // Tunisia
  'ar-YE': '🇾🇪', // Yemen
  
  // Bulgarian
  'bg-BG': '🇧🇬', // Bulgaria
  
  // Bengali
  'bn-IN': '🇮🇳', // India
  
  // Catalan
  'ca-ES': '🇪🇸', // Spain
  
  // Czech
  'cs-CZ': '🇨🇿', // Czechia
  
  // Danish
  'da-DK': '🇩🇰', // Denmark
  
  // German
  'de-AT': '🇦🇹', // Austria
  'de-CH': '🇨🇭', // Switzerland
  'de-DE': '🇩🇪', // Germany
  
  // Greek
  'el-GR': '🇬🇷', // Greece
  
  // English
  'en-AU': '🇦🇺', // Australia
  'en-CA': '🇨🇦', // Canada
  'en-GB': '🇬🇧', // United Kingdom
  'en-HK': '🇭🇰', // Hong Kong SAR
  'en-IE': '🇮🇪', // Ireland
  'en-IN': '🇮🇳', // India
  'en-KE': '🇰🇪', // Kenya
  'en-NG': '🇳🇬', // Nigeria
  'en-NZ': '🇳🇿', // New Zealand
  'en-PH': '🇵🇭', // Philippines
  'en-SG': '🇸🇬', // Singapore
  'en-TZ': '🇹🇿', // Tanzania
  'en-US': '🇺🇸', // United States
  'en-ZA': '🇿🇦', // South Africa
  
  // Spanish
  'es-AR': '🇦🇷', // Argentina
  'es-BO': '🇧🇴', // Bolivia
  'es-CL': '🇨🇱', // Chile
  'es-CO': '🇨🇴', // Colombia
  'es-CR': '🇨🇷', // Costa Rica
  'es-CU': '🇨🇺', // Cuba
  'es-DO': '🇩🇴', // Dominican Republic
  'es-EC': '🇪🇨', // Ecuador
  'es-ES': '🇪🇸', // Spain
  'es-GT': '🇬🇹', // Guatemala
  'es-HN': '🇭🇳', // Honduras
  'es-MX': '🇲🇽', // Mexico
  'es-NI': '🇳🇮', // Nicaragua
  'es-PA': '🇵🇦', // Panama
  'es-PE': '🇵🇪', // Peru
  'es-PR': '🇵🇷', // Puerto Rico
  'es-PY': '🇵🇾', // Paraguay
  'es-SV': '🇸🇻', // El Salvador
  'es-US': '🇺🇸', // United States
  'es-UY': '🇺🇾', // Uruguay
  'es-VE': '🇻🇪', // Venezuela
  
  // Estonian
  'et-EE': '🇪🇪', // Estonia
  
  // Finnish
  'fi-FI': '🇫🇮', // Finland
  
  // Filipino
  'fil-PH': '🇵🇭', // Philippines
  
  // French
  'fr-BE': '🇧🇪', // Belgium
  'fr-CA': '🇨🇦', // Canada
  'fr-CH': '🇨🇭', // Switzerland
  'fr-FR': '🇫🇷', // France
  
  // Gujarati
  'gu-IN': '🇮🇳', // India
  
  // Hebrew
  'he-IL': '🇮🇱', // Israel
  
  // Hindi
  'hi-IN': '🇮🇳', // India
  
  // Croatian
  'hr-HR': '🇭🇷', // Croatia
  
  // Hungarian
  'hu-HU': '🇭🇺', // Hungary
  
  // Indonesian
  'id-ID': '🇮🇩', // Indonesia
  
  // Icelandic
  'is-IS': '🇮🇸', // Iceland
  
  // Italian
  'it-IT': '🇮🇹', // Italy
  
  // Japanese
  'ja-JP': '🇯🇵', // Japan
  
  // Kannada
  'kn-IN': '🇮🇳', // India
  
  // Korean
  'ko-KR': '🇰🇷', // Korea
  
  // Lithuanian
  'lt-LT': '🇱🇹', // Lithuania
  
  // Latvian
  'lv-LV': '🇱🇻', // Latvia
  
  // Macedonian
  'mk-MK': '🇲🇰', // North Macedonia
  
  // Malayalam
  'ml-IN': '🇮🇳', // India
  
  // Marathi
  'mr-IN': '🇮🇳', // India
  
  // Malay
  'ms-MY': '🇲🇾', // Malaysia
  
  // Norwegian Bokmål
  'nb-NO': '🇳🇴', // Norway
  
  // Dutch
  'nl-BE': '🇧🇪', // Belgium
  'nl-NL': '🇳🇱', // Netherlands
  
  // Polish
  'pl-PL': '🇵🇱', // Poland
  
  // Portuguese
  'pt-BR': '🇧🇷', // Brazil
  'pt-PT': '🇵🇹', // Portugal
  
  // Romanian
  'ro-RO': '🇷🇴', // Romania
  
  // Russian
  'ru-RU': '🇷🇺', // Russia
  
  // Slovak
  'sk-SK': '🇸🇰', // Slovakia
  
  // Slovenian
  'sl-SI': '🇸🇮', // Slovenia
  
  // Serbian
  'sr-RS': '🇷🇸', // Serbia (Cyrillic)
  
  // Swedish
  'sv-SE': '🇸🇪', // Sweden
  
  // Swahili
  'sw-KE': '🇰🇪', // Kenya
  
  // Tamil
  'ta-IN': '🇮🇳', // India
  
  // Telugu
  'te-IN': '🇮🇳', // India
  
  // Thai
  'th-TH': '🇹🇭', // Thailand
  
  // Turkish
  'tr-TR': '🇹🇷', // Turkey
  
  // Ukrainian
  'uk-UA': '🇺🇦', // Ukraine
  
  // Urdu
  'ur-PK': '🇵🇰', // Pakistan
  
  // Vietnamese
  'vi-VN': '🇻🇳', // Vietnam
  
  // Chinese
  'zh-CN': '🇨🇳', // China (Mandarin, Simplified)
  'zh-HK': '🇭🇰', // Hong Kong (Cantonese, Traditional)
  'zh-TW': '🇹🇼', // Taiwan (Taiwanese Mandarin, Traditional)
}

/**
 * Get flag emoji for a language code based on provider
 * @param provider - 'openai' or 'azure'
 * @param languageCode - Language code (e.g., 'en' for OpenAI, 'en-US' for Azure)
 * @returns Flag emoji or globe emoji (🌐) as fallback
 */
export function getLanguageFlag(
  provider: 'openai' | 'azure',
  languageCode: string
): string {
  const flags = provider === 'openai' ? OPENAI_LANGUAGE_FLAGS : AZURE_LANGUAGE_FLAGS
  return flags[languageCode] || '🌐'
}

